import React, { ReactNode, Suspense, useMemo, useRef } from 'react';
import { observer } from 'mobx-react';
import { init as initGlobalContext } from '~/providers/mx-global-context/global-mx-context.factory';
import { init } from '~/stores/root-store.factory';
import { v4 as uuidv4 } from 'uuid';
import { RootStoreProvider } from '~/stores/root-store.provider';
import Skeleton, { SkeletonProps } from 'react-loading-skeleton';
import { useStoreCredentials } from '~/common/useStoreCredentials';
import { useRootStore } from '~/stores/useRootStore';
import { angularBootstrapStore } from '~/stores/angular-bootstrap.store';
import { trackError } from '~/services/logger/error.utils';
import { useIsInAppBrowser } from '~/common/useIsInAppBrowser';
import { safeStringify } from '~/utils/json.utils';
import { PartialUserProperties } from '~/common/user.utils';
import { PartialCompanyProperties } from '~/utils/company.utils';
import { DialogProvider } from '~/providers/dialog.provider';
import { ModalProvider } from '~/providers/modal.provider';
import { GlobalMXContextProvider } from '~/providers/mx-global-context/global-mx-context.provider';
import { useLoadFingerprintFromAngular } from '~/common/hooks/useLoadFingerprintFromAngular';
import {
    WebStreamingProviderFactory,
    LocalStorageService,
    AppLifecycleProvider
} from '@honeybook/core-web';
import {
    HttpClientProvider,
    LoggerProvider,
    AnalyticsProvider,
    StreamingProvider,
    StorageProvider,
    AppLifecycleProvider as AppLifecycleProviderComponent
} from '@honeybook/core-react';
import useAnalytics from '~/common/useAnalytics';
import { useGlobalMX } from '~/providers/mx-global-context/useGlobalMX';
import { createLogger } from '~/services/logger/logger.adapter';
import {
    GlobalAIChatProvider,
    globalAIChatStore
} from '~/providers/chat.provider';
import { HoneybookAISingletonProvider } from '@honeybook/ai-react';
import { EngagementProvider } from '@honeybook/engagement-react';
import {
    ConversationsListProvider,
    conversationsListStore
} from '@honeybook/ai-chat/conversations-list.provider';
import { WebHttpClient } from '~/common/web-http.client';

initGlobalContext();
init('mainApp');

interface Props {
    children: ReactNode | ReactNode[];
    loader?: SkeletonProps;
}

const ReactAppWrapper = observer(({ children, loader }: Props) => {
    const rootStore = useRootStore();
    const { apiService } = useGlobalMX();
    const analytics = useAnalytics();
    const appLifecycleProviderRef = useRef(new AppLifecycleProvider());
    const appLifecycleProvider = appLifecycleProviderRef.current;

    const { config, user, company, mobilePreviewMode, isOnboardingPreview } =
        angularBootstrapStore;

    useStoreCredentials({
        store: rootStore,
        config,
        user,
        company,
        mobilePreviewMode,
        isOnboardingPreview
    });

    useLoadFingerprintFromAngular();

    const isInAppBrowser = useIsInAppBrowser();

    if (isInAppBrowser && (!user || !company)) {
        trackError({
            error: new Error(
                'currUser or currCompany is not defined in mobile from angular'
            ),
            context: {
                ticketId: uuidv4(),
                user: safeStringify(user, {
                    shouldRemoveIframes: true,
                    propsToPick: PartialUserProperties
                }),
                company: safeStringify(company, {
                    shouldRemoveIframes: true,
                    propsToPick: PartialCompanyProperties
                })
            }
        });
    }

    // Create WebSocketsService for streaming provider
    const streamingProviderFactory = useMemo(
        () =>
            new WebStreamingProviderFactory({
                webSocketsService: rootStore.webSocketsService
            }),
        [rootStore.webSocketsService]
    );

    const httpClient = useMemo(
        () => new WebHttpClient(apiService),
        [apiService]
    );

    return (
        <HttpClientProvider httpClient={httpClient}>
            <StorageProvider storage={new LocalStorageService()}>
                <LoggerProvider
                    logger={createLogger({
                        featureName: 'core'
                    })}
                >
                    <AnalyticsProvider analytics={analytics}>
                        <StreamingProvider
                            streamingProviderFactory={streamingProviderFactory}
                        >
                            <EngagementProvider>
                                <GlobalAIChatProvider value={globalAIChatStore}>
                                    <AppLifecycleProviderComponent
                                        appLifecycleProvider={
                                            appLifecycleProvider
                                        }
                                    >
                                        <HoneybookAISingletonProvider>
                                            <ConversationsListProvider
                                                value={conversationsListStore}
                                            >
                                                <Suspense
                                                    fallback={
                                                        loader ? (
                                                            <Skeleton
                                                                {...loader}
                                                            />
                                                        ) : (
                                                            <div />
                                                        )
                                                    }
                                                >
                                                    {children}
                                                </Suspense>
                                            </ConversationsListProvider>
                                        </HoneybookAISingletonProvider>
                                    </AppLifecycleProviderComponent>
                                </GlobalAIChatProvider>
                            </EngagementProvider>
                        </StreamingProvider>
                    </AnalyticsProvider>
                </LoggerProvider>
            </StorageProvider>
        </HttpClientProvider>
    );
});

export default function RootStoreLayer({ children, loader }: Props) {
    return (
        <GlobalMXContextProvider>
            <RootStoreProvider>
                <DialogProvider>
                    <ModalProvider>
                        <ReactAppWrapper loader={loader}>
                            {children}
                        </ReactAppWrapper>
                    </ModalProvider>
                </DialogProvider>
            </RootStoreProvider>
        </GlobalMXContextProvider>
    );
}

// TODO: move CoreProvider here
